import { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const HEARTBEAT_INTERVAL_MS = 20000; // 20s
const IDLE_THRESHOLD_MS = 60000; // consider user idle after 60s without input

/**
 * Tracks the user's freemium timer. Sends heartbeats only while the user is
 * actively interacting with the page (mouse / keyboard within last 60s).
 *
 * Returns: { timer, loading, frozen, refresh }
 */
export function useUsageTimer() {
  const { user } = useAuth();
  const [timer, setTimer] = useState(null);
  const [loading, setLoading] = useState(true);
  const lastActivityRef = useRef(Date.now());
  const lastBeatRef = useRef(Date.now());

  // Track activity (mouse / keyboard)
  useEffect(() => {
    const onActivity = () => {
      lastActivityRef.current = Date.now();
    };
    window.addEventListener("mousemove", onActivity, { passive: true });
    window.addEventListener("keydown", onActivity);
    window.addEventListener("click", onActivity);
    return () => {
      window.removeEventListener("mousemove", onActivity);
      window.removeEventListener("keydown", onActivity);
      window.removeEventListener("click", onActivity);
    };
  }, []);

  const refresh = useCallback(async () => {
    if (!user || user.role === "admin") {
      setLoading(false);
      return null;
    }
    try {
      const { data } = await api.get("/usage/timer");
      setTimer(data);
      return data;
    } catch {
      return null;
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Initial fetch
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Heartbeat loop
  useEffect(() => {
    if (!user || user.role === "admin") return;
    const id = window.setInterval(async () => {
      const isActive = Date.now() - lastActivityRef.current < IDLE_THRESHOLD_MS;
      if (!isActive) return;
      const elapsedMs = Date.now() - lastBeatRef.current;
      const elapsedSec = Math.min(45, Math.max(1, Math.floor(elapsedMs / 1000)));
      lastBeatRef.current = Date.now();
      try {
        const { data } = await api.post("/usage/heartbeat", { elapsed_seconds: elapsedSec });
        setTimer(data);
      } catch {
        // silent
      }
    }, HEARTBEAT_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [user]);

  const frozen = Boolean(
    timer &&
      !timer.is_premium_admin &&
      timer.seconds_remaining <= 0 &&
      timer.frozen
  );

  return { timer, loading, frozen, refresh };
}
